/* eslint-disable react-hooks/exhaustive-deps */
import { ChangeEvent, useEffect, useState } from 'react';

import { useDomain, useIdentitySiteUrls } from '@netfront/gelada-identity-library';
import { Button, Select, ToggleSwitch } from '@netfront/ui-library';
import { formatWithThousandsSeparator } from 'utils';

import { CURRENCY_OPTIONS, DEFAULT_QR_CODE_PLAN_ID, QR_CODE_PRODUCT_ID, UNLIMITED_QR_CODE_PLAN_ID } from '../ProPlanCard';

import { CurrencySymbol, getPlanById, getPlanOptions, getTotalPrice, getTotalPriceWithPlanId } from './ProPlanCard.helpers';
import { ProPlanCardProps, ISelectedPlan } from './ProPlanCard.interfaces';

import { CurrencyType } from '../../interfaces';

const ProPlanCard = ({
  hasCapField = false,
  hasCTA = true,
  hasCurrencySelection,
  onPlanChange,
  onUpdateCurrency,
  plans,
  preselectedPlan,
  title = 'Calculator',
}: ProPlanCardProps) => {
  const { isDomainReady } = useDomain();
  const { getRegistrationUrl } = useIdentitySiteUrls({
    environment: process.env.REACT_APP_ENVIRONMENT,
    port: process.env.REACT_APP_IDENTITY_SITE_LOCAL_PORT,
  });

  const [currency, setCurrency] = useState<CurrencyType>('AUD');
  const [hasUnlimitedPlan, setHasUnlimitedPlan] = useState<boolean>(false);
  const [isPayAsYouGo, setIsPayAsYouGo] = useState<boolean>(false);
  const [registrationUrl, setRegistrationUrl] = useState<string>();
  const [selectedProductPlan, setSelectedProductPlan] = useState<ISelectedPlan>({
    plan: preselectedPlan ?? getPlanById(DEFAULT_QR_CODE_PLAN_ID, plans),
  });

  const handlePayAsYouGoChange = () => {
    setIsPayAsYouGo(!isPayAsYouGo);
  };

  const handleSelectChange = ({ target: { id, selectedIndex } }: ChangeEvent<HTMLSelectElement>) => {
    const { id: selectedPlanId } = getPlanOptions(plans, Number(id))[selectedIndex - 1];

    setSelectedProductPlan({
      plan: getPlanById(Number(selectedPlanId), plans),
    });
  };

  const handleCurrencySelectChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const {
      target: { value },
    } = event;

    const selectedCurrency = value as CurrencyType;

    onUpdateCurrency(selectedCurrency);
    setCurrency(selectedCurrency);
  };

  useEffect(() => {
    if (!isDomainReady) {
      return;
    }

    setRegistrationUrl(getRegistrationUrl());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDomainReady]);

  useEffect(() => {
    if (!isPayAsYouGo) {
      return;
    }

    setSelectedProductPlan({
      plan: getPlanById(UNLIMITED_QR_CODE_PLAN_ID, plans),
    });
  }, [isPayAsYouGo]);

  useEffect(() => {
    if (!onPlanChange) {
      return;
    }

    const hasUnlimitedPlanSelected = selectedProductPlan.plan?.id === UNLIMITED_QR_CODE_PLAN_ID;

    setHasUnlimitedPlan(hasUnlimitedPlanSelected);

    onPlanChange({
      ...selectedProductPlan,
      totalPrice: hasUnlimitedPlanSelected ? 0 : getTotalPrice(selectedProductPlan),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProductPlan]);

  const { plan: qrCodePlan } = selectedProductPlan;

  const planOptions = getPlanOptions(plans, QR_CODE_PRODUCT_ID);
  const selectedPlan = planOptions.find((r) => r.id == qrCodePlan?.id);

  return (
    <>
      {hasCurrencySelection && (
        <div className="c-plan-calculator__select-wrapper">
          <div className="c-plan-calculator__select-input-container">
            <Select
              additionalClassNames="c-plan-calculator__select"
              id="currencySelect"
              labelText={currency}
              name="currencySelect"
              options={CURRENCY_OPTIONS}
              value={currency}
              isLabelHidden
              onChange={handleCurrencySelectChange}
            />
          </div>
        </div>
      )}
      <div className="c-plan-calculator">
        <div className="c-plan-calculator__header">
          <h4 className="c-plan-calculator__title">{title}</h4>
          <div className="c-plan-calculator__price">
            {isPayAsYouGo || hasUnlimitedPlan ? (
              <>
                <p className="h2 font-header weight-600 text-uppercase mb-0 ">Starting $0</p>
                <span className="font-header">per month</span>
              </>
            ) : (
              <>
                <p className="h2 font-header weight-600 text-uppercase mb-0">{`${CurrencySymbol[currency]}${formatWithThousandsSeparator(
                  getTotalPriceWithPlanId(
                    selectedProductPlan,
                    plans,
                  )
                )}`}</p>
                <span className="font-header">per month</span>
              </>
            )}
          </div>
        </div>

        <div className="c-plan-calculator__body">
          {hasCapField && (
            <div className="c-plan-calculator__row">
              <ToggleSwitch id="payg" isChecked={isPayAsYouGo} labelText="Pay as you go" onChange={handlePayAsYouGoChange} />
            </div>
          )}

          <div className="c-plan-calculator__row">
            {!isPayAsYouGo && (
              <Select
                additionalClassNames="c-plan-calculator__select"
                id={String(QR_CODE_PRODUCT_ID)}
                labelText="QR Code"
                name="qrCode"
                options={planOptions}
                value={selectedPlan?.value ?? 0}
                onChange={handleSelectChange}
              />
            )}
          </div>

          {hasCTA && (
            <div className="c-plan-calculator__footer">
              {registrationUrl && <Button linkButton={{ id: 'register', url: registrationUrl }} text="Get started" />}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export { ProPlanCard };
